/* ==== band-b.js ==== */
/* ============================================================
   BAND B — Session 3's counter work (#ppb → #box).

   Liquid figures.

   The digits do not count. They are the number, from the moment
   you see them. The only motion is the level rising through the
   glyphs once, behind a wave that travels exactly as long as the
   fill does and then stops.

   Almost all of it is CSS: --odo-fill and --odo-phase are registered
   custom properties with a transition and a keyframe on them, so
   the browser interpolates both. This file only decides *when*,
   and builds the one element CSS cannot conjure — the liquid
   layer, which needs the same glyphs a second time.

   Owns [data-odo]. app.js still owns [data-count] and is
   untouched; the attribute is the handover, so neither system can
   reach the other's elements.
   ============================================================ */
(() => {
'use strict';

const nodes = [...document.querySelectorAll('[data-odo]')];
if (!nodes.length) return;

/* The liquid is a second copy of the glyphs stacked on the first.
   It is aria-hidden because the real text is already on the parent
   and a screen reader should hear the figure once, not twice. Only
   after it exists does the parent dim to the empty vessel colour —
   until then the parent is the whole figure, which is what keeps
   this correct with JS blocked. */
nodes.forEach(el => {
  const liq = document.createElement('span');
  liq.className = 'odo__liq';
  liq.setAttribute('aria-hidden', 'true');
  liq.textContent = el.textContent;
  el.appendChild(liq);
  el.classList.add('is-lined');
  el._liq = liq;
});

if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

/* Emptying them is itself a change to --fill, so it would animate:
   the figure would spend 1.8s draining from full before it ever
   filled. Suppress the transition for that one step, flush the
   style, then hand it back. The reflow read is load-bearing —
   without it the browser coalesces both writes into one frame,
   never sees the empty state, and nothing transitions afterwards. */
nodes.forEach(el => {
  el._liq.style.transition = 'none';
  /* -8%, not 0%: the wave wobbles ±0.06em around the waterline, so a level
     parked exactly at the bottom lets crests peek above the floor and light
     the low-hanging comma while the digits are still empty. Parked a step
     below, empty is empty. */
  el._liq.style.setProperty('--odo-fill', '-8%');
});
void document.body.offsetHeight;
nodes.forEach(el => { el._liq.style.transition = ''; });

function fill(el) {
  if (el._filled) return;
  el._filled = true;
  el.classList.add('is-filling');          /* starts the wave */
  el._liq.style.setProperty('--odo-fill', '100%');
}

const io = new IntersectionObserver((es) => {
  es.forEach(e => { if (e.isIntersecting) { fill(e.target); io.unobserve(e.target); } });
}, { threshold: 0.6 });
nodes.forEach(el => io.observe(el));

/* The same safety net app.js carries, for the same reason:
   IntersectionObserver does not fire in a hidden document, so a
   background-tab load would leave these figures sitting empty —
   and empty, here, means a headline number in 13% ink. */
function sweep() {
  const vh = innerHeight;
  nodes.forEach(el => {
    if (el._filled) return;
    const r = el.getBoundingClientRect();
    if (r.top < vh && r.bottom > 0) { fill(el); io.unobserve(el); }
  });
}
addEventListener('visibilitychange', () => { if (!document.hidden) sweep(); });
addEventListener('load', () => setTimeout(sweep, 1200));

})();

/* ==== band-c.js ==== */
/* ============================================================
   BAND C — Session 3 only.
   Sections 10-14: modes, day, certs, specs, box.

   Owns [data-h2] — the dissolved-hydrogen field in #certs.
   app.js owns [data-bubbles] and never sees these, so the two
   decorative systems cannot fight over the same nodes.

   The §17 charge-time counter that used to lead this file moved
   to band-d.js when the page went from three bands to four.
   That also removed a real bug: the H2 builder used to sit after
   an early `if (!nodes.length) return;` guard on [data-fill-to],
   so deleting one number in §17 would have silently killed this
   whole field.
   ============================================================ */
(() => {
'use strict';

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- §12 · dissolved H₂ field ----------
   Builds the diatomic field that replaces the bubbles in #certs.

   Owns [data-h2]. app.js owns [data-bubbles] and never sees these, so the
   two decorative systems cannot fight over the same nodes.

   No IntersectionObserver and no requestAnimationFrame here on purpose: the
   nodes are built once at parse time and the motion is pure CSS, so there is
   nothing for a hidden document to stall. That is why this needs none of the
   guards the counter above does. */
if (!reduced) {
  document.querySelectorAll('[data-h2]').forEach(field => {
    const n = +field.dataset.h2;
    const frag = document.createDocumentFragment();

    for (let i = 0; i < n; i++) {
      const m = document.createElement('span');
      m.className = 'h2m';

      /* Atom diameter, so a molecule spans about 1.6× this. Tuned by looking
         at it magnified and at true scale: below about 6px the pair stops
         resolving as two spheres and becomes an anonymous speck, which is
         just a smaller bubble and not worth the change. Above about 11px it
         starts competing with the certification marks it sits behind. */
      const a = 6.5 + Math.random() * 4.5;
      /* Nearer molecules are slightly larger, slightly more opaque and
         slightly faster. Tying the three together gives depth for free;
         randomising them independently just looks noisy. */
      const near = (a - 6.5) / 4.5;

      m.style.setProperty('--a', a.toFixed(2) + 'px');
      m.style.setProperty('--o', (0.13 + near * 0.13).toFixed(3));
      m.style.setProperty('--dx', (Math.random() * 70 - 35).toFixed(0) + 'px');
      m.style.setProperty('--spin', (Math.random() * 220 - 110).toFixed(0) + 'deg');
      m.style.left = (Math.random() * 100).toFixed(2) + '%';
      m.style.animationDuration = (26 - near * 7 + Math.random() * 8).toFixed(1) + 's';
      /* Negative delays start the field mid-flight, so the section is already
         populated on arrival instead of filling from the bottom edge. */
      m.style.animationDelay = (-Math.random() * 34).toFixed(1) + 's';

      frag.appendChild(m);
    }
    field.appendChild(frag);
  });
}

})();

/* ==== band-d.js ==== */
/* ============================================================
   BAND D — Session 4 only.
   Sections 15-17: reviews, faq, buy.

   One figure: the charge time in §17. It fills as it counts, so
   the number and the level are the same value read two ways.

   Owns [data-fill-to]. app.js owns [data-count], Band B owns
   [data-odo], Band C owns [data-h2]; the attribute is the
   handover, so no two systems can reach the same element or
   fight over textContent.

   Split out of band-c.js when the page went from three bands to
   four. #buy is Band D; #certs stayed in band-c.js.
   ============================================================ */
(() => {
'use strict';

const nodes = [...document.querySelectorAll('[data-fill-to]')];
if (!nodes.length) return;

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const DUR = 1600;   /* matches Band B, so the page has one counter tempo */

/* Digits: quartic ease-out. Strictly monotonic and clamped — a duration that
   overshoots would flash a charge time the product does not have. */
const digits = (p) => 1 - Math.pow(1 - p, 4);

/* Surface: the same rise with a small damped ripple on top, so the level
   finds itself rather than arriving. 5% amplitude, gone well before the end.
   This is the only part allowed to overshoot, because a liquid surface does
   and a number doesn't. */
const level = (p) => p >= 1 ? 1
  : digits(p) + Math.sin(p * Math.PI * 3) * Math.pow(1 - p, 3) * 0.05;

/* Not zero-padded, on measurement. Padding looked right in principle — both
   glyph slots always occupied — but this face's "0" is nearly twice the width
   of its "1", so "00" renders 122px against the 92px the ghost reserves for
   "18". Overflowing 15px a side closed the word spaces and the headline read
   "in00minutes". Unpadded, the widest value the count passes through is "10"
   at 95px: 1.5px a side, invisible. */
nodes.forEach(el => {
  const target = +el.dataset.fillTo;
  const val = el.querySelector('.fillnum__val');
  if (!val) return;
  /* The markup ships full and correct for the no-JS case. Now that JS is
     running, empty it so there is something to fill. */
  /* --fill goes on `val`, not on `el`. Band B registers --fill globally with
     `inherits: false`, so a value written to the parent never reaches the
     child that reads it — the child would fall back to the registered
     initial-value of 100% and the figure would sit permanently full. */
  if (!reduced) { val.textContent = '0'; val.style.setProperty('--fill', '0%'); }
  el._fill = { target, val, done: reduced };
});

function run(el) {
  const s = el._fill;
  if (!s || s.done) return;
  s.done = true;

  const t0 = performance.now();
  const step = (t) => {
    const p = Math.min((t - t0) / DUR, 1);
    s.val.textContent = Math.round(s.target * digits(p));
    s.val.style.setProperty('--fill', (level(p) * 100).toFixed(2) + '%');
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

const io = new IntersectionObserver((es) => {
  es.forEach(e => { if (e.isIntersecting) { run(e.target); io.unobserve(e.target); } });
}, { threshold: 0.6 });
nodes.forEach(el => io.observe(el));

/* The same safety net app.js carries, for the same reason: neither
   IntersectionObserver nor requestAnimationFrame runs in a hidden document,
   so a background-tab load would leave this figure sitting at 0 and empty
   until something moved. */
function sweep() {
  const vh = innerHeight;
  nodes.forEach(el => {
    if (!el._fill || el._fill.done) return;
    const r = el.getBoundingClientRect();
    if (r.top < vh && r.bottom > 0) { run(el); io.unobserve(el); }
  });
}
addEventListener('visibilitychange', () => { if (!document.hidden) sweep(); });
addEventListener('load', () => setTimeout(sweep, 1200));

})();


/* ============================================================
   §15 — the review rotator and the photo strip.

   Ported from the shape of the armor.shop product page: a truncated
   photo strip with a "See More", and one review at a time with
   arrows, keys, swipe and autoplay, rather than the three-up masonry
   grid that was here before and had no way to reach a fourth review.

   Owns [data-rev] and its data-rev-* children. Band D already owns
   [data-fill-to]; app.js owns [data-count]/[data-bubbles]/[data-meter]/
   [data-bar]/[data-marquee]/[data-total], Band B owns [data-odo],
   Band C owns [data-h2]. No overlap, so nothing else can reach these
   nodes or fight over their contents.

   Both widgets ship complete and correct with JS off — every slide in
   flow as a readable stack, every photo in the strip visible — and
   this file removes rather than adds. Nothing below assumes it ran.
   ============================================================ */
(() => {
'use strict';

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------------- §17 · dissolved-H₂ field ----------------
   Owns [data-molecules]. Deliberately not [data-h2]: band-c.js queries that
   document-wide, so a data-h2 here would put a Band D element under Band C's
   builder. Attribute selectors match exact names, so [data-h2] cannot reach
   [data-molecules] and the two fields stay independent.

   No IntersectionObserver and no requestAnimationFrame, for the same reason
   band-c.js has none: the nodes are built once at parse time and the motion
   is pure CSS, so there is nothing a hidden document can stall.
   ---------------------------------------------------------------- */
if (!reduced) {
  document.querySelectorAll('[data-molecules]').forEach(field => {
    const n = +field.dataset.molecules;
    const frag = document.createDocumentFragment();

    for (let i = 0; i < n; i++) {
      const m = document.createElement('span');
      m.className = 'h2buy__m';

      /* Atom diameter; a molecule spans 1.62× this, so 7.5–13 here is a
         12–21px object. Larger than §12's 6.5–11 on purpose — that field
         sits behind six certification marks and has to yield to them, while
         this section has nothing small to compete with. Below ~7px the pair
         stops resolving as two spheres and the whole point of the change is
         lost; it just becomes a smaller bubble. */
      const a = 8 + Math.random() * 5;
      const near = (a - 8) / 5;

      /* Size, opacity, blur and speed all move together on one depth value.
         Randomising them independently reads as noise; tying them gives
         parallax for free — bigger, brighter, faster and sharper all mean
         "nearer", which is the only cue a flat field has. */
      m.style.setProperty('--a', a.toFixed(2) + 'px');
      m.style.setProperty('--o', (0.12 + near * 0.14).toFixed(3));
      /* Depth of field, scaled to the atom rather than fixed in px — a blur
         that is a constant number of pixels is a light touch on a 13px sphere
         and a catastrophe on an 8px one.

         The first pass used up to 0.9px, which on the smallest atom is 11% of
         its diameter. Magnified 6× that molecule was a single smear: the blur
         had closed the notch between the two spheres and undone the only
         thing separating a molecule from a bubble. Capped at 4% of the atom,
         the far end reads soft while the pair still resolves. */
      m.style.setProperty('--blur', ((1 - near) * a * 0.04).toFixed(2) + 'px');
      m.style.setProperty('--dx', (Math.random() * 70 - 35).toFixed(0) + 'px');
      m.style.setProperty('--spin', (Math.random() * 200 - 100).toFixed(0) + 'deg');

      /* Edge-weighted horizontal placement. A uniform scatter puts molecules
         straight through the headline, the price and the two buttons — the
         four things this section exists for. This curve pushes the
         distribution outward smoothly: it never draws a hard empty corridor
         (which would read as a mistake), it just makes the middle the least
         likely place to be. Exponent < 1 biases toward the edges; 0.62 was
         picked so roughly a fifth of the field still crosses the centre
         third and the drift does not look like it is avoiding something. */
      const r = Math.random() - 0.5;
      const x = 0.5 + Math.sign(r) * Math.pow(Math.abs(r) * 2, 0.62) * 0.5;
      m.style.left = (x * 100).toFixed(2) + '%';

      /* 24–37s. Slower than §12's 19–34s: this is the closing frame and a
         slow rise reads as still water, where a quicker one reads as fizz. */
      m.style.animationDuration = (31 - near * 7 + Math.random() * 6).toFixed(1) + 's';
      /* Negative delays start the field mid-flight, so the section is already
         populated on arrival instead of filling from the bottom edge. */
      m.style.animationDelay = (-Math.random() * 38).toFixed(1) + 's';

      frag.appendChild(m);
    }
    field.appendChild(frag);
  });
}

/* ---------------- the photo strip ---------------- */
(() => {
  const strip = document.querySelector('[data-rev-strip]');
  if (!strip) return;
  const wrap  = strip.closest('.rvw-strip__wrap');
  const more  = strip.querySelector('[data-rev-more]');
  const extra = [...strip.querySelectorAll('[data-rev-extra]')];

  if (more && extra.length) {
    extra.forEach(el => { el.hidden = true; });
    more.hidden = false;
    more.addEventListener('click', () => {
      extra.forEach(el => { el.hidden = false; });
      /* The tile the button sits on is a real photo, not a backdrop. Removing
         the overlay hands that photo back instead of leaving a dead scrim on
         it — which is what happens if you only disable the button. */
      more.remove();
      edges();
    }, { once: true });
  }

  /* Only now is the native scrollbar suppressed. Everything below is the
     replacement for it, so the class and the controls arrive together — if
     this file never runs, the bar is still there and the row still moves. */
  strip.classList.add('is-enh');

  const prev = wrap && wrap.querySelector('[data-rev-strip-nav="prev"]');
  const next = wrap && wrap.querySelector('[data-rev-strip-nav="next"]');

  /* 1px of slack. Browsers report fractional scroll positions, so an exact
     `left > 0` test leaves the prev arrow showing at rest on some widths. */
  const SLACK = 1;

  function edges() {
    const max = strip.scrollWidth - strip.clientWidth;
    const atStart = strip.scrollLeft <= SLACK;
    const atEnd   = strip.scrollLeft >= max - SLACK;
    const overflowing = max > SLACK;
    if (prev) prev.hidden = !overflowing || atStart;
    if (next) next.hidden = !overflowing || atEnd;
    /* The mask replaces the scrollbar's "there is more" job. It is only ever
       applied on the side that actually has more, so a row scrolled to the
       end is not left with a phantom fade against nothing. */
    strip.style.setProperty('--fade-l', (!overflowing || atStart) ? '0px' : '32px');
    strip.style.setProperty('--fade-r', (!overflowing || atEnd)   ? '0px' : '32px');
  }

  function page(dir) {
    /* A whole viewport minus one tile, so the photo you were looking at stays
       on screen as an anchor instead of the row jumping past it. */
    const tile = strip.querySelector('.rvw-strip__i');
    const step = Math.max(strip.clientWidth - (tile ? tile.getBoundingClientRect().width : 0), 120);
    /* No `behavior` here on purpose. .rvw-strip declares scroll-behavior in
       CSS and the reduced-motion query flips it to auto, so the preference is
       honoured in one place. Passing it from JS too would override the query
       and quietly reintroduce the animation for people who asked for none. */
    strip.scrollBy({ left: dir * step });
  }

  if (prev) prev.addEventListener('click', () => page(-1));
  if (next) next.addEventListener('click', () => page(1));
  strip.addEventListener('scroll', edges, { passive: true });
  addEventListener('resize', edges);
  edges();
  /* Tiles are lazy images: the row's scrollWidth grows as they arrive, and a
     strip measured before that decides it does not overflow. */
  addEventListener('load', edges);
})();

/* ---------------- the rotator ---------------- */
(() => {
  const root = document.querySelector('[data-rev]');
  if (!root) return;

  const slides = [...root.querySelectorAll('[data-rev-slide]')];
  const track  = root.querySelector('[data-rev-track]');
  const dots   = root.querySelector('[data-rev-dots]');
  /* One slide is a stack of one — there is nothing to page, so leave the
     markup alone rather than building arrows that cannot go anywhere. */
  if (slides.length < 2 || !track) return;

  const HOLD = 6000;
  let idx = 0, timer = null, inView = false;
  /* Reduced motion means no autoplay at all, not a slower autoplay: the
     objection is to the page moving on its own, and the arrows still work. */
  let stopped = reduced;

  /* Pips are built here, not in the markup: with JS off the slides are a
     stack and nothing pages, so a row of dots would describe a control
     that does not exist.

     Above a dozen slides they stop being pips. The real feed is 67 reviews,
     and .rvw__dots is an unwrapped flex row of 6px items — 67 of them do not
     overflow, they shrink to 2px slivers that read as a smudge and cannot be
     counted. Past the threshold the row becomes a plain "n / 67" instead,
     which is the same information in the space actually available. */
  const MANY = 12;
  const many = slides.length > MANY;
  let pips = [], counter = null;
  if (many) {
    dots.classList.add('rvw__dots--count');
    counter = dots.appendChild(document.createElement('b'));
  } else {
    pips = slides.map(() => dots.appendChild(document.createElement('i')));
  }

  /* Animated paging. The current card is the only one in flow; the rest sit
     absolute and invisible (see .rvw__track.is-live). A page slides the
     outgoing card toward where it's going and the incoming card in from the
     opposite side, while the track's height eases between the two cards —
     auto→auto can't animate, so the height is set inline here. Under
     prefers-reduced-motion the CSS strips the transitions and this same code
     degrades to an instant swap. */
  const STATE = ['is-cur', 'is-set', 'is-from-r', 'is-from-l', 'is-to-l', 'is-to-r'];

  function setH() { track.style.height = slides[idx].offsetHeight + 'px'; }

  function show(n, dir) {
    const from = slides[idx];
    idx = (n + slides.length) % slides.length;
    const to = slides[idx];
    if (to !== from) {
      const d = dir || 1;
      slides.forEach(s => { if (s !== from && s !== to) s.classList.remove(...STATE); });
      from.classList.remove(...STATE);
      from.classList.add(d > 0 ? 'is-to-l' : 'is-to-r');
      to.classList.remove(...STATE);
      to.classList.add('is-set', d > 0 ? 'is-from-r' : 'is-from-l');
      void to.offsetWidth;               /* commit the start position */
      to.classList.remove('is-set', 'is-from-r', 'is-from-l');
      to.classList.add('is-cur');
    } else {
      to.classList.add('is-cur');
    }
    setH();
    if (many) counter.textContent = (idx + 1) + ' / ' + slides.length;
    else pips.forEach((p, k) => p.classList.toggle('is-on', k === idx));
  }

  function play() {
    if (stopped || timer || !inView || document.hidden) return;
    timer = setInterval(() => { if (!document.hidden) show(idx + 1, 1); }, HOLD);
  }
  function pause() { if (timer) { clearInterval(timer); timer = null; } }

  /* Manual navigation ends autoplay permanently. Someone who has taken the
     arrows is reading at their own pace; having the panel move under them
     six seconds later is the single most irritating thing a carousel does. */
  function manual(dir) { stopped = true; pause(); show(idx + dir, dir); }

  root.querySelectorAll('[data-rev-nav]').forEach(btn => {
    btn.addEventListener('click', () => manual(btn.dataset.revNav === 'prev' ? -1 : 1));
  });

  /* Fires while focus is inside the panel — the arrows are real buttons, so
     that is reachable by tab without making the panel itself a tab stop. */
  root.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft')       { manual(-1); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { manual(1);  e.preventDefault(); }
  });

  let sx = 0, sy = 0;
  track.addEventListener('touchstart', (e) => {
    sx = e.changedTouches[0].clientX; sy = e.changedTouches[0].clientY;
  }, { passive: true });
  track.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - sx;
    const dy = e.changedTouches[0].clientY - sy;
    /* Horizontal intent only. A finger travelling mostly downward is a page
       scroll that happened to start on the panel, and paging the rotator on
       it makes the section feel like it is grabbing the gesture. */
    if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.5) manual(dx < 0 ? 1 : -1);
  }, { passive: true });

  root.addEventListener('pointerenter', pause);
  root.addEventListener('pointerleave', play);
  root.addEventListener('focusin',  pause);
  root.addEventListener('focusout', play);

  track.classList.add('is-live');
  show(0);

  /* Long bodies clamp to six lines with a Read-more (the reference does the
     same). Applied only where the text actually overflows — a five-word
     review gets no dead button. The test measures the NATURAL height against
     six line-heights BEFORE clamping: scrollHeight on an already-clamped
     element is a trap — Chrome's line-clamp lays the hidden continuation out
     degenerately and reports ~50 phantom lines, which flagged 65 of 67
     reviews. Runs after is-live so every slide has the track's width, and
     again at fonts.ready since the threshold moves with real font metrics.
     Opening a review is reading at your own pace, so the toggle also ends
     autoplay, same as taking the arrows. */
  function clampPass() {
    slides.forEach(s => {
      const body = s.querySelector('.rvw-item__body');
      if (!body || body.dataset.clampDone) return;
      const cs = getComputedStyle(body);
      const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.6;
      if (body.getBoundingClientRect().height <= lh * 6 + 2) return;
      body.dataset.clampDone = '1';
      body.classList.add('is-clamp');
      const btn = document.createElement('button');
      btn.type = 'button'; btn.className = 'rvw-more'; btn.textContent = 'Read more';
      btn.addEventListener('click', () => {
        const clamped = body.classList.toggle('is-clamp');
        btn.textContent = clamped ? 'Read more' : 'Read less';
        stopped = true; pause();
        setH();
      });
      body.after(btn);
    });
    setH();
  }
  clampPass();
  if (document.fonts && document.fonts.status !== 'loaded') {
    document.fonts.ready.then(clampPass);
  }

  /* The height is set in pixels, so anything that reflows the cards has to
     re-measure — viewport changes and late-loading fonts/images. */
  addEventListener('resize', setH);
  addEventListener('load', setH);

  const io = new IntersectionObserver((es) => {
    es.forEach(e => { inView = e.isIntersecting; if (inView) play(); else pause(); });
  }, { threshold: 0.4 });
  io.observe(root);

  /* The same net the counters carry, for the same reason: an
     IntersectionObserver does not fire in a hidden document, so on a
     background-tab load inView would stay false and the rotator would sit
     on slide one forever once the tab came forward. */
  function sweep() {
    const r = root.getBoundingClientRect();
    inView = r.top < innerHeight && r.bottom > 0;
    if (inView) play();
  }
  addEventListener('visibilitychange', () => { if (document.hidden) pause(); else { sweep(); play(); } });
  addEventListener('load', () => setTimeout(sweep, 1200));
})();

/* ---------------- the overlays ----------------
   Everything below is a native <dialog>. showModal() supplies the focus
   trap, the Esc key, the inertness of the page behind and the ::backdrop,
   so none of that is reimplemented — the code here is only about what is
   inside the box. */
(() => {
  const dlgs = [...document.querySelectorAll('.rvw-mod, .rvw-lb')];
  if (!dlgs.length) return;

  function open(d) { if (d && !d.open) d.showModal(); }
  function close(d) { if (d && d.open) d.close(); }

  document.querySelectorAll('[data-rev-open]').forEach(btn => {
    btn.addEventListener('click', () => open(document.getElementById(btn.dataset.revOpen)));
  });

  dlgs.forEach(d => {
    d.querySelectorAll('[data-rev-close]').forEach(b => b.addEventListener('click', () => close(d)));
    /* Click-outside-to-close. The dialog element fills the viewport and the
       visible card is a child, so a click landing on the dialog itself is a
       click on the backdrop — no scrim element and no geometry maths. */
    d.addEventListener('click', (e) => { if (e.target === d) close(d); });
  });

  /* ---- the all-reviews list ----
     Cloned from the rotator rather than written out again. Two copies of the
     same reviews in markup is two things to update when the live feed lands,
     and they drift the first time someone edits one. */
  const list = document.querySelector('[data-rev-all-list]');
  const empty = document.querySelector('[data-rev-all-empty]');
  const slides = [...document.querySelectorAll('[data-rev-slide]')];

  if (list && slides.length) {
    slides.forEach(s => {
      const c = s.cloneNode(true);
      c.removeAttribute('data-rev-slide');   /* the rotator must not find it */
      c.hidden = false;                      /* it arrives carrying the rotator's state */
      /* …including the animation classes and the clamp. The modal list is a
         plain list: no slide states, full text, no Read-more button. */
      c.classList.remove('is-cur', 'is-set', 'is-from-r', 'is-from-l', 'is-to-l', 'is-to-r');
      const cb = c.querySelector('.rvw-item__body');
      if (cb) cb.classList.remove('is-clamp');
      const cm = c.querySelector('.rvw-more');
      if (cm) cm.remove();
      /* Read the rating off the clone so the filter has something to match.
         It comes from the visible figure, not a second hand-kept attribute. */
      const score = c.querySelector('.rvw-item__score .mono');
      c.dataset.revScore = score ? String(Math.round(parseFloat(score.textContent))) : '';
      list.appendChild(c);
    });

    const chips = [...document.querySelectorAll('[data-rev-filter]')];
    const items = [...list.children];

    /* A filter that offers a rating nothing matches is a dead end, so counts
       are taken once and empty ratings are disabled rather than hidden —
       "no 2-star reviews" is itself useful information. */
    chips.forEach(chip => {
      const v = chip.dataset.revFilter;
      if (v === 'all') return;
      if (!items.some(i => i.dataset.revScore === v)) chip.disabled = true;
    });

    /* ---- filter + pagination, one engine ----
       Ten a page. The chips choose the set, the pager walks it; a filter
       change re-pages from 1, and a page click scrolls the modal body back
       to the top so page 3 doesn't open mid-list. */
    const PAGE = 10;
    let filt = 'all', page = 1;

    const pager = document.createElement('nav');
    pager.className = 'rvw-pager';
    pager.setAttribute('aria-label', 'Review pages');
    list.after(pager);
    const scrollBox = list.closest('.rvw-mod__body');

    function pageBtn(label, target, opts) {
      const b = document.createElement('button');
      b.type = 'button'; b.textContent = label;
      if (opts.on)  { b.classList.add('is-on'); b.setAttribute('aria-current', 'page'); }
      if (opts.dis) b.disabled = true;
      if (opts.aria) b.setAttribute('aria-label', opts.aria);
      b.addEventListener('click', () => {
        page = target;
        apply();
        if (scrollBox) scrollBox.scrollTop = 0;
      });
      return b;
    }

    function apply() {
      const match = items.filter(i => filt === 'all' || i.dataset.revScore === filt);
      const pages = Math.max(1, Math.ceil(match.length / PAGE));
      if (page > pages) page = pages;
      const from = (page - 1) * PAGE;
      const vis = new Set(match.slice(from, from + PAGE));
      items.forEach(i => { i.hidden = !vis.has(i); });
      if (empty) empty.hidden = match.length > 0;

      pager.textContent = '';
      pager.hidden = pages < 2;
      if (pages < 2) return;
      pager.appendChild(pageBtn('‹', page - 1, { dis: page === 1, aria: 'Previous page' }));
      for (let p = 1; p <= pages; p++) {
        /* Above seven pages only 1, the last, and page±1 stay; each skipped
           run collapses to one ellipsis. At 67 reviews this never fires, but
           the live feed will grow past it. */
        if (pages > 7 && p !== 1 && p !== pages && Math.abs(p - page) > 1) {
          if (p === 2 || p === pages - 1) {
            const gap = document.createElement('span');
            gap.textContent = '…';
            pager.appendChild(gap);
          }
          continue;
        }
        pager.appendChild(pageBtn(String(p), p, { on: p === page, aria: 'Page ' + p }));
      }
      pager.appendChild(pageBtn('›', page + 1, { dis: page === pages, aria: 'Next page' }));
    }

    chips.forEach(chip => chip.addEventListener('click', () => {
      if (chip.disabled) return;
      filt = chip.dataset.revFilter;
      page = 1;
      chips.forEach(c => c.classList.toggle('is-on', c === chip));
      apply();
    }));

    apply();
  }

  /* ---- tabs ---- */
  const tabs = [...document.querySelectorAll('[data-rev-tab]')];
  const panels = [...document.querySelectorAll('[data-rev-panel]')];
  function selectTab(name) {
    tabs.forEach(t => {
      const on = t.dataset.revTab === name;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', String(on));
    });
    panels.forEach(p => { p.hidden = p.dataset.revPanel !== name; });
  }
  tabs.forEach(t => t.addEventListener('click', () => selectTab(t.dataset.revTab)));
  /* Left/right arrows move between tabs, which is what the tablist role
     promises a screen-reader user it will do. */
  tabs.forEach((t, i) => t.addEventListener('keydown', (e) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const n = tabs[(i + d + tabs.length) % tabs.length];
    n.focus(); selectTab(n.dataset.revTab);
  }));

  /* ---- gallery + lightbox ----
     Sourced from the strip and from every review's own media, deduped by
     URL, so a photo attached to a review is not also listed separately. */
  const gal = document.querySelector('[data-rev-all-gallery]');
  const lb = document.getElementById('rvw-lb');
  const lbImg = document.querySelector('[data-rev-lb-img]');
  let shots = [], at = 0;

  if (gal) {
    const seen = new Set();
    document.querySelectorAll('[data-rev-strip] img, .rvw-item__media img').forEach(img => {
      const src = img.getAttribute('src');
      if (!src || seen.has(src)) return;
      seen.add(src);
      shots.push({ src, alt: img.getAttribute('alt') || '' });
    });

    shots.forEach((s, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', 'Open photo ' + (i + 1) + ' of ' + shots.length);
      const im = document.createElement('img');
      im.src = s.src; im.alt = s.alt; im.loading = 'lazy'; im.decoding = 'async';
      b.appendChild(im);
      b.addEventListener('click', () => { at = i; paint(); open(lb); });
      gal.appendChild(b);
    });

    /* Every review photo on the page becomes a button that opens the
       lightbox on itself — the strip tiles, the thumbnails on the rotator
       cards, and the clones of those cards in the modal list (already in
       the DOM by now). Wrapped at runtime so the no-JS page stays honest:
       with no lightbox to open, a photo stays a photo, not a dead button. */
    document.querySelectorAll('[data-rev-strip] .rvw-strip__i > img, .rvw-item__media img').forEach(img => {
      const i = shots.findIndex(s => s.src === img.getAttribute('src'));
      if (i < 0) return;
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'rvw-shot'; b.dataset.shot = String(i);
      b.setAttribute('aria-label', 'Enlarge photo: ' + (img.getAttribute('alt') || 'review photo'));
      img.replaceWith(b);
      b.appendChild(img);
    });
    /* Delegated, so a .rvw-shot works no matter which container it's in. */
    document.addEventListener('click', (e) => {
      const b = e.target.closest('.rvw-shot');
      if (!b) return;
      at = +b.dataset.shot; paint(); open(lb);
    });
  }

  function paint() {
    if (!lbImg || !shots.length) return;
    lbImg.src = shots[at].src;
    lbImg.alt = shots[at].alt;
    lb.setAttribute('aria-label', 'Photo ' + (at + 1) + ' of ' + shots.length);
  }
  function step(d) { if (shots.length) { at = (at + d + shots.length) % shots.length; paint(); } }

  document.querySelectorAll('[data-rev-lb-nav]').forEach(b => {
    b.addEventListener('click', () => step(b.dataset.revLbNav === 'prev' ? -1 : 1));
  });
  /* Esc is already handled by <dialog>; only the arrows need adding. */
  if (lb) lb.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft')  { step(-1); e.preventDefault(); }
    if (e.key === 'ArrowRight') { step(1);  e.preventDefault(); }
  });
})();

/* ---------------- the review form ----------------
   Validates for real. It does not post, because there is no endpoint on a
   static page, and it does not append a review to the list either — this
   page's reviews are already flagged as sample data and quietly inventing
   more from a form would make that worse. On a valid submit it reports the
   payload it assembled, which is the thing whoever wires the backend needs.
   ---------------------------------------------------------------- */
(() => {
  const form = document.querySelector('[data-rev-form]');
  if (!form) return;

  const done    = document.querySelector('[data-rev-done]');
  const payload = document.querySelector('[data-rev-payload]');
  const drop    = form.querySelector('[data-rev-drop]');
  const file    = form.querySelector('#rvw-files');
  const thumbs  = form.querySelector('[data-rev-preview]');
  const counter = form.querySelector('[data-rev-count]');
  const body    = form.querySelector('#rvw-body');

  const MAX_FILES = 5;
  const MAX_BYTES = 5 * 1024 * 1024;
  let picked = [];

  if (body && counter) {
    const sync = () => { counter.textContent = String(body.value.length); };
    body.addEventListener('input', sync); sync();
  }

  function err(name, msg) {
    const p = form.querySelector('[data-rev-err="' + name + '"]');
    const field = form.querySelector('[name="' + name + '"]');
    if (p) { p.textContent = msg || ''; p.hidden = !msg; }
    if (field && field.classList) field.classList.toggle('is-bad', !!msg);
    return !msg;
  }

  /* ---- files ---- */
  function renderThumbs() {
    thumbs.textContent = '';
    picked.forEach((f, i) => {
      const li = document.createElement('li');
      const im = document.createElement('img');
      /* Object URLs, revoked on load — a data URL for a 5 MB photo would sit
         in memory as a base64 string a third larger than the file. */
      const url = URL.createObjectURL(f);
      im.src = url; im.alt = f.name;
      im.addEventListener('load', () => URL.revokeObjectURL(url), { once: true });
      const x = document.createElement('button');
      x.type = 'button'; x.textContent = '×';
      x.setAttribute('aria-label', 'Remove ' + f.name);
      x.addEventListener('click', () => { picked.splice(i, 1); renderThumbs(); });
      li.append(im, x);
      thumbs.appendChild(li);
    });
  }

  function addFiles(fs) {
    /* Every rejection is collected, not just the last one. Dropping a folder
       that trips two different rules and being told about one of them sends
       you round the loop again for a reason you were never shown. */
    const bad = [];
    [...fs].forEach(f => {
      if (picked.length >= MAX_FILES) { bad.push(f.name + ': over the ' + MAX_FILES + '-photo limit'); return; }
      if (!f.type.startsWith('image/')) { bad.push(f.name + ': not an image'); return; }
      if (f.size > MAX_BYTES) { bad.push(f.name + ': over 5 MB'); return; }
      picked.push(f);
    });
    err('photos', bad.length ? 'Skipped ' + bad.length + ': ' + bad.join('; ') : '');
    renderThumbs();
  }

  if (file) file.addEventListener('change', () => {
    addFiles(file.files);
    /* Cleared so re-picking the same file fires change again. */
    file.value = '';
  });

  if (drop) {
    ['dragenter', 'dragover'].forEach(t => drop.addEventListener(t, (e) => {
      e.preventDefault(); drop.classList.add('is-over');
    }));
    ['dragleave', 'drop'].forEach(t => drop.addEventListener(t, (e) => {
      e.preventDefault(); drop.classList.remove('is-over');
    }));
    drop.addEventListener('drop', (e) => {
      if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files);
    });
  }

  /* ---- submit ---- */
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const d = new FormData(form);
    const get = (k) => String(d.get(k) || '').trim();

    const ok = [
      err('rating', d.get('rating') ? '' : 'Pick a rating.'),
      err('title',  get('title')  ? '' : 'Add a title.'),
      err('body',   get('body').length >= 10 ? '' : 'Tell us a little more, at least 10 characters.'),
      err('name',   get('name')   ? '' : 'Add your name.'),
      /* Deliberately loose. Tight email regexes reject real addresses, and
         the only check that settles it is sending mail to it. */
      err('email',  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(get('email')) ? '' : 'Check the email address.')
    ].every(Boolean);

    if (!ok) {
      const bad = form.querySelector('.is-bad, [data-rev-err]:not([hidden])');
      if (bad) (bad.closest('.rvw-field') || bad).scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
      return;
    }

    /* Photos are optional, so a skipped file never blocks the submit — but
       leaving its warning on screen while the form succeeds reads as "it
       went through despite an error". The rejected files are simply not in
       the payload, which the panel below shows. */
    err('photos', '');

    err('send', '');

    /* The endpoint is the adivaa-reviews Cloudflare Worker (source in
       tools/review-worker/) — an INBOX, not a publisher: submissions are
       read by the store owner and hand-carried into Loox after the same
       moderation every review above went through. Photos ride along as
       real files; the worker enforces the same 5-file/5MB caps the picker
       does. */
    const fd = new FormData();
    fd.append('rating', String(Number(d.get('rating'))));
    fd.append('title', get('title'));
    fd.append('body', get('body'));
    fd.append('name', get('name'));
    fd.append('email', get('email'));
    fd.append('page', location.pathname.split('/').pop() || 'landing');
    picked.forEach((f) => fd.append('photos', f, f.name));

    const sbtn = form.querySelector('button[type="submit"]');
    if (sbtn) { sbtn.disabled = true; sbtn.setAttribute('aria-busy', 'true'); }
    fetch('https://adivaa-reviews.iftekhar-shaikh.workers.dev/review', { method: 'POST', body: fd })
      .then((r) => r.json().catch(() => ({})).then((res) => {
        if (!r.ok || !res.ok) throw new Error(res.error || 'HTTP ' + r.status);
        form.hidden = true;
        if (done) done.hidden = false;
      }))
      .catch(() => {
        /* The review text is still in the fields — nothing is lost, and
           the retry is one click. */
        err('send', 'Could not send your review just now. Please try again in a moment.');
      })
      .finally(() => {
        if (sbtn) { sbtn.disabled = false; sbtn.removeAttribute('aria-busy'); }
      });
  });
})();

})();


/* ============================================================
   BAND D — cart.

   The page had twelve href="#buy" anchors and no cart. This binds the
   three that actually promise a purchase and leaves the other nine
   scrolling, which is what they were always for.

   Why this lives in Band D when two of those three buttons are Band A's
   markup: this file is loaded document-wide, so the behaviour can be
   attached without editing the hero or the sticky buybar. That is the
   same arrangement the buybar's show/hide already uses. Band A's files
   are not touched, and this ships in hm-3.js — so the two seats never
   push the same file.

   Owns [data-hm-cart], [data-prot] and [data-cart-err]. It does NOT
   touch [data-total]: that belongs to app.js, which is shared and
   frozen, and two writers on one element is exactly the collision the
   band split exists to prevent. The hero's price therefore does not
   move when a plan is chosen — the plan's own price is stated on the
   control, and the cart is where the arithmetic is shown for real.

   Progressive enhancement throughout: with JS off, or if the config
   element is ever dropped from §17, every one of these anchors is still
   a working link to the buy section.
   ============================================================ */
(() => {
'use strict';

const cfg = document.querySelector('[data-hm-cart]');
const MAIN = cfg && cfg.dataset.variant;
if (!MAIN) return;          /* no config -> anchors stay anchors */

/* Cart intent is decided by class, not by link text. The three buttons that
   offer to sell something are the two --primary ones (hero, buybar) and the
   one --onDeep one (§17). Every other #buy anchor on the page is --ghost, a
   reel chip or an inline link, and all of those are navigation: "Book a
   demo", "Get all three", "Talk to us before you buy". Matching on class
   keeps a copy change from silently turning a link into a checkout. */
const CART_SEL = 'a[href="#buy"].btn--primary, a[href="#buy"].btn--onDeep';

const errEl = document.querySelector('[data-cart-err]');
const buy = document.getElementById('buy');

/* --------------------- Razorpay Magic Checkout ---------------------
   Magic Checkout arrives store-wide through magic-sopc.js (the Razorpay app
   embed) on {{ content_for_header }}, so it loads on this isolated layout too.
   It hijacks clicks -- at document capture, with no isTrusted gate -- on
   Shopify's own cart-checkout controls: [name="checkout"], and a[href="/checkout"]
   inside a cart form (form[action="/cart"] / .cart-form / .rzp-sidecart).
   Nothing on this page matches those selectors: the drawer's Checkout is an
   absolute-URL anchor OUTSIDE any cart form, and every Buy now is a plain #buy
   link. So Magic never fires here on its own -- which is what keeps the page
   dormant until we opt in, exactly like the GoKwik hook this replaces.

   The entry point is window.openRzpLogin() -- Magic's own trigger, exposed by
   magic-sopc.js (verified 2026-08-28: it preventDefaults, re-fetches /cart.js
   with no-cache, then opens the login->checkout on that cart). It re-reads the
   cart at call time, so add()'s bottle + plan are already in the cart it opens
   -- exactly the shape GoKwik's initiateLogin() had. It needs BOTH the "Magic
   Checkout Script" and "Login with Razorpay" app embeds enabled; with the login
   embed off the function exists but no-ops.

   goMagic() returns false when Magic is absent or the call throws, so every
   caller falls back to the native /checkout it would have used -- the button is
   never dead. (A synthesised a[href="/checkout"] click was tried first and does
   NOT work: magic-sopc only acts on checkout controls it registered at load, so
   an element created and clicked in the same tick is never intercepted.)

   Dormant by default: ships off, turned on per visitor with ?magic=1
   (remembered) and off with ?magic=0. Flip MAGIC_DEFAULT to true to make Magic
   Checkout everyone's checkout. */
const MAGIC_DEFAULT = false;

const mFlag = new URLSearchParams(location.search).get('magic');
let magicOn = MAGIC_DEFAULT;
try {
  if (mFlag === '1') localStorage.setItem('adivaa:magic', '1');
  else if (mFlag === '0') localStorage.removeItem('adivaa:magic');
  magicOn = MAGIC_DEFAULT || localStorage.getItem('adivaa:magic') === '1';
} catch (e) { magicOn = MAGIC_DEFAULT || mFlag === '1'; }
if (mFlag === '0') magicOn = false;

function goMagic() {
  if (typeof window.openRzpLogin !== 'function') return false;
  try { window.openRzpLogin(); return true; } catch (e) { return false; }
}

/* ----------------------------- GoKwik checkout -----------------------------
   Same dormant-accelerator contract as Magic above, for GoKwik's KwikCheckout
   (the kwik-cod-checkout app embed, wired store-side 2026-09-04). It also
   rides {{ content_for_header }}, so its SDK loads on this isolated layout
   too — but its OWN triggers never fire here: the PDP path serializes
   form[action="/cart/add"] and the drawer path swaps stock checkout buttons
   ([name="checkout"], .cart__checkout-button, ...), and this page has
   neither. Dormant by construction, exactly like Magic.

   The entry point is window.initiateLogin() (gokwik-sso-sdk.js, deferred in
   the head). Verified 2026-09-04: it re-fetches /cart.js at call time before
   opening the login modal, so add()'s bottle + plan are already in the cart
   it opens. On an EMPTY cart it silently no-ops (and leaves body overflow
   hidden), so every caller here adds first or checks cartHasItems().

   goKwik() mirrors goMagic(): false when the SDK is absent or the call
   throws, so every caller falls back to the native /checkout it would have
   used — the button is never dead.

   DEFAULT ON since 2026-09-04, flipped after Iftekhar's live end-to-end
   testing. ?gokwik=0 is the per-visitor kill switch; setting GOKWIK_DEFAULT
   back to false stands the page down to native for everyone. With both
   flags on, GoKwik wins — one accelerator per click. */
const GOKWIK_DEFAULT = true;

const gkFlag = new URLSearchParams(location.search).get('gokwik');
let gokwikOn = GOKWIK_DEFAULT;
try {
  if (gkFlag === '1') localStorage.setItem('adivaa:gokwik', '1');
  else if (gkFlag === '0') localStorage.removeItem('adivaa:gokwik');
  gokwikOn = GOKWIK_DEFAULT || localStorage.getItem('adivaa:gokwik') === '1';
} catch (e) { gokwikOn = GOKWIK_DEFAULT || gkFlag === '1'; }
if (gkFlag === '0') gokwikOn = false;

function goKwik() {
  /* Two GoKwik entry points exist on this page, tried best-first.
     1. window.gokwikSdk.initCheckout(merchantInfo) — the FULL one-page
        checkout (payments inline). Comes from merchant.integration.js,
        loaded by gk-merchant-lite.liquid in this page's isolated layout,
        with window.merchantInfo set synchronously in the head. Reads the
        live /cart at open.
     2. window.initiateLogin() — the kwik-cod-checkout app embed's SSO
        flow (login + address, then a handoff to native checkout). Only a
        fallback: if merchant.integration.js hasn't loaded by click time,
        this still gets the shopper to a working register.
     Both absent or throwing -> false, and the caller's native /checkout
     fallback runs. */
  try {
    if (window.gokwikSdk && typeof window.gokwikSdk.initCheckout === 'function' &&
        window.merchantInfo && window.merchantInfo.mid) {
      window.gokwikSdk.initCheckout(window.merchantInfo);
      return true;
    }
    if (typeof window.initiateLogin === 'function') {
      window.initiateLogin();
      return true;
    }
  } catch (e) { /* fall through to native */ }
  return false;
}

/* One accelerator per click; native /checkout when neither is on. */
const accelOn = () => gokwikOn || magicOn;
function goAccel() { return gokwikOn ? goKwik() : goMagic(); }

/* Magic opens over a live page (modal), so unlike a native buy-now it can be
   pressed twice. cartHasItems() lets a repeat click reopen Magic on the cart
   already built instead of adding a second bottle. */
async function cartHasItems() {
  try {
    const r = await fetch('/cart.js', { credentials: 'same-origin' });
    return r.ok && (await r.json()).item_count > 0;
  } catch (e) { return false; }
}

function release(btn) {
  btn.classList.remove('is-busy');
  btn.removeAttribute('aria-busy');
  busy = false;
}

function say(msg) {
  if (!errEl) return;
  errEl.textContent = msg || '';
  errEl.hidden = !msg;
}

/* null when "No protection" is selected — the empty value is the point, so
   the default adds one line item and not two. */
function plan() {
  const on = document.querySelector('[data-prot] input[name="hm-protection"]:checked');
  return on && on.value ? Number(on.value) : null;
}

/* Band A's hero carries its OWN care-plan control, name="careplan", and
   app.js folds the chosen plan into [data-total] — the button promises
   (unit + plan) × qty. The control's values are PRICES (0/799/1149), not
   variant ids, because the protection app mints its sp- products with
   millisecond timestamps and reaps them: any hardcoded id dies (the hidden
   [data-prot] fieldset below is the tombstone). So the id is minted at
   runtime through Protega's own creation endpoint (below). Read-only
   against Band A's control on purpose. */
function heroPlanPrice() {
  const on = document.querySelector('input[name="careplan"]:checked');
  const v = on ? Number(on.value) : 0;
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/* Protega's REAL creation path, read out of its own widget bundle
   (/apps/protega-app/storefront/main.js, 2026-08-15): the widget never
   scans products.json. It quotes prices from api.protega.ai/offer, then
   POSTs /apps/protega-app/api/addToCartAPI — same-origin app proxy, no
   auth beyond being on the storefront — and THAT response carries the plan
   variant's gid directly. The /offer call alone stopped materialising sp-
   products (variant_12m_id comes back null now), which is why scraping the
   product list found nothing and the card hid on a healthy store. So this
   page mints exactly like the widget: quote, then create, and read the id
   out of the response instead of hunting for it. */
const planQuote = new Map();   /* price → { term, title }, from the quote  */
const planVariant = new Map(); /* price → minted variant id, this session  */

async function quotePlans() {
  if (planQuote.size) return planQuote;
  const pid = cfg && cfg.dataset.product;
  if (!pid) return planQuote;
  try {
    const r = await fetch('https://api.protega.ai/api/shopifyApp/offer', {
      /* a hung quote must not leave the card pending forever */
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(8000) : undefined,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_id: pid, variant_id: String(MAIN), shop_url: location.origin })
    });
    const d = await r.json();
    if (!d || !d.success || !d.accidental) return planQuote;
    const title = (d.productDetails && d.productDetails.title) || '';
    for (const term of [12, 24]) {
      const q = d.accidental['Protection' + term + 'mAccidental'];
      const price = q ? Math.round(parseFloat(q.price)) : 0;
      if (price > 0 && title) planQuote.set(price, { term, title });
    }
  } catch (_) { /* no quote, no card — the visibility pass below decides */ }
  return planQuote;
}

/* Mint (or re-mint after a reap) the plan variant for a displayed price.
   The price sent is only ever Protega's own quote for that term: if the
   quote and the radio disagree, the price never enters the map and the
   plan simply isn't offered — charging anything other than the number on
   the card is worse than not selling cover. The title comes from the
   quote too, so the product is HydroMax-named by construction. */

/* Protega's own widget caches minted plan ids in localStorage for 20
   minutes (key protega_plan_cache_<product>_<variant>, one entry per term
   holding planVariantId/price/timestamp — read out of its bundle,
   2026-09-04), so a same-browser reload does not mint two more catalog
   products. This page uses the SAME key and shape: mints are shared with
   the official widget in both directions, and a warm-cache reload mints
   nothing. A cache hit is also always older than Protega's post-mint
   fixup window (see the add retry in add()), so cached ids never trip the
   "sold out" lag. A cached id Protega has meanwhile reaped is caught by
   the add retry, which drops the cache and re-mints. The price must match
   the cached entry exactly — a repriced plan never rides a stale id. */
const PROT_TTL = 20 * 60 * 1000;
function protCacheKey(pid, vid) { return 'protega_plan_cache_' + pid + '_' + vid; }
function protCacheRead(pid, vid, term, price) {
  try {
    const all = JSON.parse(localStorage.getItem(protCacheKey(pid, vid)) || '{}');
    const e = all[String(term)];
    return e && e.planVariantId && e.price === price &&
      e.timestamp && Date.now() - e.timestamp < PROT_TTL ? Number(e.planVariantId) : null;
  } catch (_) { return null; }
}
function protCacheWrite(pid, vid, term, price, id) {
  try {
    const k = protCacheKey(pid, vid);
    const all = JSON.parse(localStorage.getItem(k) || '{}');
    all[String(term)] = { planVariantId: id, price: price, timestamp: Date.now() };
    localStorage.setItem(k, JSON.stringify(all));
  } catch (_) { /* cache is best-effort */ }
}
function protCacheDrop(pid, vid) {
  try { localStorage.removeItem(protCacheKey(pid, vid)); } catch (_) {}
}

async function mintPlanVariant(price) {
  if (planVariant.has(price)) return planVariant.get(price);
  const q = (await quotePlans()).get(price);
  if (!q) return null;
  const pid = cfg && cfg.dataset.product;
  const cached = protCacheRead(pid, MAIN, q.term, price);
  if (cached) { planVariant.set(price, cached); return cached; }
  try {
    const r = await fetch('/apps/protega-app/api/addToCartAPI', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_id: Number(pid), variant_id: Number(MAIN),
        plan_type: 'accidental', price: price, shop_link: location.origin,
        linkedToProductID: Number(pid), _linkedToProductVariantid: Number(MAIN),
        name: (q.term === 12 ? '1 Year' : '2 Year') + ' Accidental Protection for ' + q.title,
        type: 'accidental', term: q.term
      })
    });
    const d = await r.json();
    const id = d && d.success && d.planVariantId
      ? Number(String(d.planVariantId).replace(/\D/g, '')) : null;
    if (id) { planVariant.set(price, id); protCacheWrite(pid, MAIN, q.term, price, id); }
    return id;
  } catch (_) { return null; }
}

/* The hero's stepper is Band A's; read what the customer set, never write. */
function heroQty() {
  const out = document.querySelector('[data-qty-value]');
  const n = out ? parseInt(out.textContent, 10) : 1;
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/* Dynamic pricing (2026-10-05, Protega's ask): the card ships with no
   prices. Each paid radio carries data-term (12 / 24); the quote for THIS
   product fills its value and its [data-plan-price] label, so a reprice
   (which moves Protega's price band) needs no page edit, and the price on
   the card is by construction the price minted. A term the quote lacks is
   hidden. The card leaves .plan--pending once painted, and a change event
   lets app.js re-total. Returns the prices on offer. */
function paintPlans(card, quote) {
  const byTerm = new Map([...quote].map(([price, q]) => [q.term, price]));
  card.querySelectorAll('input[data-term]').forEach((r) => {
    const price = byTerm.get(Number(r.dataset.term)) || 0;
    const opt = r.closest('.plan__opt');
    const label = opt && opt.querySelector('[data-plan-price]');
    r.value = String(price);
    r.disabled = !price;
    if (label) label.textContent = '+₹' + price.toLocaleString('en-IN');
    if (opt) opt.style.display = price ? '' : 'none';
  });
  card.classList.remove('plan--pending');
  const on = card.querySelector('input:checked');
  if (on) on.dispatchEvent(new Event('change', { bubbles: true }));
  return [...byTerm.values()];
}

/* The care plan is only OFFERED while it can actually be rung up. At load,
   mint both variants up front (the official widget prefetches the same
   way), which both decides the card's visibility and makes click time a
   cache hit. Hidden rather than disabled when nothing mints: "No plan"
   stays checked, totals stay honest, and the card returns by itself on the
   next load once Protega answers again. */
(async () => {
  const card = document.querySelector('.plan');
  if (!card) return;
  const prices = paintPlans(card, await quotePlans());
  if (!prices.length) { card.hidden = true; return; }
  const minted = await Promise.all(prices.map(mintPlanVariant));
  if (minted.every(v => !v)) card.hidden = true;
})();

let busy = false;

/* Signature (qty|plan) of what the last Magic buy-now put in the cart, so a
   reopened modal doesn't add a second bottle. Empty for Add-to-cart. */
let magicCart = '';

async function add(btn, dest) {
  if (busy) return;
  busy = true;
  say('');
  btn.classList.add('is-busy');
  btn.setAttribute('aria-busy', 'true');

  const qty = heroQty();
  /* The bottle and the plan are added in TWO calls, in that order, because
     /cart/add.js is not atomic: items before a failing line ARE added. One
     combined call meant a plan failure could leave bottles in the cart and
     the retry then added them AGAIN. Bottle first: if IT fails (sold out),
     nothing was added and the register's own message shows. Plan second, at
     QUANTITY 1 — Protega's minted plan products are quantity-capped at 1
     (measured 2026-08-16: a fresh mint refuses qty 2 with a 422, on both
     stores' plans), so one cover per order is the only sellable contract
     and app.js prices the total the same way. If the plan can't be added
     (reaped mid-session), one fresh mint and one retry, then the sale goes
     through bottle-only — a missing add-on must never park the customer. */
  const price = heroPlanPrice();
  const p = plan();

  /* Accelerator buy-now only: same qty+plan as last time and the cart still
     holds it -> reopen the modal on that cart instead of adding again.
     Verified against the live cart, since the drawer can empty it between
     clicks. */
  const sig = dest === '/cart' ? '' : qty + '|' + (p || '');
  if (accelOn() && sig && sig === magicCart && await cartHasItems()) {
    if (!goAccel()) location.assign(dest);
    release(btn);
    return;
  }

  const postCart = (list) => fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ items: list })
  });

  try {
    const bottle = [{ id: Number(MAIN), quantity: qty }];
    if (p) bottle.push({ id: p, quantity: 1 });
    const r = await postCart(bottle);
    /* Shopify answers a sold-out or bad-variant add with 422 and a human
       sentence in `description`. Prefer it over anything invented here. */
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      throw new Error(e.description || e.message || 'Could not add that to the cart.');
    }
    if (price) {
      /* Protega fixed the old sold-out lag on 2026-09-10: addToCartAPI now
         BLOCKS until the variant is genuinely sellable before returning its
         id (a pre-seeded/warm SKU ~1.5s, a cold new-price SKU ~4-5s), and
         dedups to one permanent SKU per product+term+price. So a freshly
         minted variant now adds clean on the FIRST try — the old
         "deny -> continue" wait (a 422 "already sold out" for ~5-15s after
         the id came back) is gone, re-verified 2026-09-10. The only residual
         failure is a CACHED id (localStorage / in-memory) that Protega has
         since reaped: that add 422s, so drop the caches, re-mint a fresh
         (now-sellable) variant and retry with a small cushion in case the
         fixup ever runs late again. A total miss still falls through
         bottle-only — a missing add-on must never park the customer. */
      const addPlan = async (v, waits) => {
        if (!v) return false;
        for (const w of waits) {
          if (w) await new Promise((res) => setTimeout(res, w));
          const pr = await postCart([{ id: v, quantity: 1 }]);
          if (pr.ok) return true;
        }
        return false;
      };
      let planOk = await addPlan(await mintPlanVariant(price), [0]);
      if (!planOk) {
        planVariant.delete(price);
        planQuote.clear();
        protCacheDrop(cfg && cfg.dataset.product, MAIN);
        planOk = await addPlan(await mintPlanVariant(price), [0, 2000]);
      }
      /* !planOk here -> bottle-only sale, deliberately silent */
    }
    /* "Add to cart" no longer navigates: the drawer IS the cart. Band A
       listens for hm:cart:open and re-fetches before showing, so the item
       is already in the list as it slides in. Only "Buy now" (/checkout)
       still leaves the page — there the navigation is the completion and
       the button stays busy on purpose. */
    if (dest === '/cart') {
      document.dispatchEvent(new Event('hm:cart:open'));
      release(btn);
    } else if (accelOn()) {
      /* The accelerator reads the live /cart we just built and opens over
         the page; if it's unavailable, fall back to the native checkout. */
      magicCart = sig;
      if (!goAccel()) location.assign(dest);
      release(btn);
    } else {
      location.assign(dest);
    }
  } catch (err) {
    say(err && err.message ? err.message : 'Could not reach the cart. Please try again.');
    /* The message lives in §17. A failure on the hero button, 6000px up, is
       otherwise invisible — so fall back to what the anchor would have done
       anyway and bring the reader to it. */
    if (errEl) errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    else if (buy) buy.scrollIntoView({ behavior: 'smooth' });
    release(btn);
  }
}

document.addEventListener('click', (e) => {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const btn = e.target.closest(CART_SEL);
  if (!btn) return;
  e.preventDefault();
  /* (The old refuse-rather-than-undercharge block died here: with a plan
     selected it errored and scrolled the customer to §17 — which read as
     "Buy now navigated me to Ask a human". Plans resolve at runtime now,
     inside add(), and the click always heads for the register.) */
  /* "Buy now" is the only CTA on the page that promises to skip the cart.
     The other two say "Add to cart" and land on the cart, so each button
     does the thing it is labelled with. Default is the cart, so a copy
     change can only ever cost a click — never skip one. */
  add(btn, /buy\s*now/i.test(btn.textContent) ? '/checkout' : '/cart');
});

/* The cart drawer's Checkout button (Band A's markup: an absolute-URL anchor
   to /checkout, which matches none of Magic's cart-form selectors). With Magic
   on, open Magic on the cart the drawer is already showing; with Magic off the
   native anchor is left completely alone. Delegated because the drawer is
   Band A's. */
document.addEventListener('click', (e) => {
  if (!accelOn()) return;
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  if (!e.target.closest('.cartd__checkout')) return;
  /* Only swallow the native anchor if the accelerator actually opened. */
  if (goAccel()) e.preventDefault();
});

})();

/* ---------------- chrome vs footer, and the drawer's perks ----------------
   Two small pieces of housekeeping that belong to nobody's section:

   1. While the footer is on screen, fixed chrome stands down — the buybar
      slides out (CSS class, rule in the Band D region) and the CheqUPI
      widget fades. The WhatsApp rail already does this on its own; this
      brings the rest of the floating layer to the same manners. The CheqUPI
      button is a third-party embed outside .hm, so scoped CSS can't reach
      it — inline styles are the only clean handle. It also loads late and
      on its own schedule, hence the patient retry.

   2. The CheqUPI button aligns to the WhatsApp rail's right edge (both are
      54px circles, so same right edge = same centre line).

   3. The cart drawer (Band A's component — nothing of Band A is edited)
      gets a perks block injected into its footer: the free gift and the
      three promises. Inside [data-cart-foot] so it only shows when the
      cart has items. */
(() => {
  'use strict';

  /* ---- perks into the drawer foot ---- */
  const foot = document.querySelector('[data-cart-foot]');
  if (foot && !foot.querySelector('.cartd-perks')) {
    const perks = document.createElement('div');
    perks.className = 'cartd-perks';
    perks.innerHTML =
      '<ul class="cartd-perks__row" role="list">' +
      '<li>365-day replacement</li><li>30-day returns</li><li>Free shipping in India</li>' +
      '</ul>';
    foot.prepend(perks);
  }
  /* The drawer ships with Shopify's boilerplate "Shipping and taxes are
     calculated at checkout", which contradicts this page twice over: shipping
     is free across India and the price is MRP, taxes included. The markup is
     Band A's, so the line is corrected here at runtime rather than edited at
     the source. */
  const note = document.querySelector('.cartd__note');
  if (note) note.textContent = 'Free shipping across India. Price includes all taxes.';

  /* ---- footer observer ---- */
  const footer = document.querySelector('.foot');
  if (!footer || !('IntersectionObserver' in window)) return;

  let atFoot = false;

  const chq = () => document.getElementById('chqme-widget-btn');

  function dressChq() {
    const b = chq();
    if (!b) return false;
    if (!b.dataset.hmDressed) {
      b.dataset.hmDressed = '1';
      b.style.transition = 'opacity .25s ease, visibility .25s ease';
      /* Align to the WhatsApp rail. Both are 54px circles, so matching the
         right edge matches the centre line. Skipped quietly if the rail is
         hidden or absent — a wrong guess would be worse than the 20px gap. */
      const rail = document.getElementById('frail');
      if (rail) {
        const r = rail.getBoundingClientRect();
        if (r.width > 0) b.style.right = Math.max(8, Math.round(innerWidth - r.right)) + 'px';
      }
    }
    b.style.opacity = atFoot ? '0' : '';
    b.style.visibility = atFoot ? 'hidden' : '';
    b.style.pointerEvents = atFoot ? 'none' : '';
    return true;
  }
  addEventListener('resize', () => dressChq());

  new IntersectionObserver(([e]) => {
    atFoot = e.isIntersecting;
    dressChq();
  }, { threshold: 0.04 }).observe(footer);

  /* The widget script loads whenever it loads. Poll gently, stop as soon as
     it's dressed or after 20s — a page without the widget owes it nothing. */
  let tries = 0;
  const t = setInterval(() => {
    if (dressChq() || ++tries > 40) clearInterval(t);
  }, 500);
})();

/* ---------------- demo CTAs → the real booking widget ----------------
   The store already ships a "book a demo" experience (the CheqUPI widget,
   the circle button bottom-right — "See it live. Decide better."). Every
   demo-intent link on the page should open THAT, not a chat link:

   - the §17 "Book a demo video call" button (marked data-demo), and
   - any other anchor whose label says "book a demo", wherever it lives —
     matched at click time so no other band's markup is edited.

   When the widget isn't on the page (local preview, embed disabled) the
   links keep their hrefs and simply do what they say. The widget button
   may be faded out by the footer observer above — a programmatic .click()
   doesn't care about pointer-events, so booking still works from the
   footer's neighbourhood. */
(() => {
  'use strict';
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest('a');
    if (!a) return;
    if (!a.hasAttribute('data-demo') && !/book\s+a\s+demo/i.test(a.textContent)) return;
    const w = document.getElementById('chqme-widget-btn');
    if (!w) return;                 /* no widget → the href does its job */
    e.preventDefault();
    w.click();
  });
})();


